/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import {
  ReactFlowEdge,
  ReactFlowNode,
  TransformEngineNodeHandle,
  PARENT_HANDLE_ID,
} from "../Util/TransformEngineDataUtils";
import {
  NodeDescriptor,
  InputNodeDescriptor,
  OutputNodeDescriptor,
  TransformNodeDescriptor,
  FileType,
  TransformScope,
} from "Api";
import { PathType } from "../../../Api/DataTransformation";
import { Connection } from "@xyflow/react";
import { NODE_TYPES } from "../../../Shared/TransformNodeTypes";
import { loggedNotify, Component } from "../../../Shared/Notify";

/**
 * Rule validation result
 */
export interface RuleValidationResult {
  isValid: boolean;
  errorMessage?: string;
  warningMessage?: string;
  severity?: "error" | "warning" | "info";
}

/**
 * Rule context for connection validation
 */
export interface ConnectionRuleContext {
  sourceNode: ReactFlowNode;
  targetNode: ReactFlowNode;
  sourceHandle?: TransformEngineNodeHandle;
  targetHandle?: TransformEngineNodeHandle;
  existingEdges: ReactFlowEdge[];
  allNodes: ReactFlowNode[];
}

/**
 * Rule context for node validation
 */
export interface NodeRuleContext {
  node: ReactFlowNode;
  allNodes: ReactFlowNode[];
  allEdges: ReactFlowEdge[];
}

/**
 * Rule context for edge validation
 */
export interface EdgeRuleContext {
  edge: ReactFlowEdge;
  allEdges: ReactFlowEdge[];
  allNodes: ReactFlowNode[];
}

/**
 * Base class for Transform Engine Rules
 * Contains general rules that apply to all transform types
 */
export abstract class TransformEngineRules {
  /**
   * Validate a potential connection between two nodes
   * @param connection The proposed connection
   * @param context Additional context needed for validation
   * @returns Validation result
   */
  public validateConnection(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // Run all general connection rules
    const generalResult = this.runGeneralConnectionRules(connection, context);
    if (!generalResult.isValid) {
      return generalResult;
    }

    // Run transform-specific connection rules (implemented by subclasses)
    const specificResult = this.runSpecificConnectionRules(connection, context);
    if (!specificResult.isValid) {
      return specificResult;
    }

    return { isValid: true };
  }

  /**
   * Validate a node
   * @param context Node validation context
   * @returns Validation result
   */
  public validateNode(context: NodeRuleContext): RuleValidationResult {
    // Run general node rules
    const generalResult = this.runGeneralNodeRules(context);
    if (!generalResult.isValid) {
      return generalResult;
    }

    // Run transform-specific node rules
    const specificResult = this.runSpecificNodeRules(context);
    if (!specificResult.isValid) {
      return specificResult;
    }

    return { isValid: true };
  }

  /**
   * Validate an edge
   * @param context Edge validation context
   * @returns Validation result
   */
  public validateEdge(context: EdgeRuleContext): RuleValidationResult {
    // Run general edge rules
    const generalResult = this.runGeneralEdgeRules(context);
    if (!generalResult.isValid) {
      return generalResult;
    }

    // Run transform-specific edge rules
    const specificResult = this.runSpecificEdgeRules(context);
    if (!specificResult.isValid) {
      return specificResult;
    }

    return { isValid: true };
  }

  /**
   * Emit a notification based on the validation result
   * @param result The validation result
   */
  public emitNotification(
    result: RuleValidationResult,
    axiosContext: any
  ): void {
    if (!result.isValid) {
      if (result.severity === "warning" && result.warningMessage) {
        loggedNotify.warning(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          result.warningMessage
        );
      } else if (result.errorMessage) {
        loggedNotify.negative(
          axiosContext,
          Component.TRANSFORM_ENGINE,
          result.errorMessage
        );
      }
    } else if (result.warningMessage) {
      loggedNotify.warning(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        result.warningMessage
      );
    } else if (result.severity === "info" && result.errorMessage) {
      loggedNotify.info(
        axiosContext,
        Component.TRANSFORM_ENGINE,
        result.errorMessage
      );
    }
  }

  /**
   * Check if a connection is valid and emit notification if not
   * @param connection The proposed connection
   * @param context Additional context
   * @returns Whether the connection is valid
   */
  public isConnectionValid(
    connection: Connection,
    context: ConnectionRuleContext,
    axiosContext: any
  ): boolean {
    const result = this.validateConnection(connection, context);

    if (!result.isValid || result.warningMessage) {
      this.emitNotification(result, axiosContext);
    }

    return result.isValid;
  }

  // ============================================================================
  // GENERAL CONNECTION RULES (apply to all transforms)
  // ============================================================================

  protected runGeneralConnectionRules(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // Rule 1: Cannot connect a node to itself
    const selfConnectionResult = this.checkSelfConnection(connection);
    if (!selfConnectionResult.isValid) return selfConnectionResult;

    // Rule 2: Source and target must exist
    const nodeExistenceResult = this.checkNodesExist(context);
    if (!nodeExistenceResult.isValid) return nodeExistenceResult;

    // Rule 3: Cannot create duplicate connections
    const duplicateResult = this.checkDuplicateConnection(connection, context);
    if (!duplicateResult.isValid) return duplicateResult;

    // Rule 4: Cannot connect an input node to an output node
    const inputToOutputResult = this.checkInputToOutputConnection(context);
    if (!inputToOutputResult.isValid) return inputToOutputResult;

    // Rule 5: Cannot create cycles (optional, can be overridden)
    const cycleResult = this.checkForCycles(connection, context);
    if (!cycleResult.isValid) return cycleResult;

    // Rule 6: Cannot connect edges to mismatching node family types
    const familyTypeConnectionResult = this.checkConnectionFamilyType(context);
    if (!familyTypeConnectionResult.isValid) return familyTypeConnectionResult;

    // Rule 7: Cannot connect a transform to an output node that already has a type defined and the output type does not match
    const outputNodeTypeResult = this.checkOutputTypeMatch(context);
    if (!outputNodeTypeResult.isValid) return outputNodeTypeResult;

    // Rule 8: Full-file data may only feed full-file transforms, and
    // granular data may only feed granular transforms.
    const scopeMatchResult = this.checkTransformScopeMatch(connection, context);
    if (!scopeMatchResult.isValid) return scopeMatchResult;

    // We can add rules as time goes on

    return { isValid: true };
  }

  protected checkSelfConnection(connection: Connection): RuleValidationResult {
    if (connection.source === connection.target) {
      return {
        isValid: false,
        errorMessage: "Cannot connect a node to itself",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  protected checkConnectionFamilyType(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const sourceNode = context.sourceNode;
    const targetNode = context.targetNode;

    if (
      sourceNode.type == NODE_TYPES.input &&
      targetNode.type != NODE_TYPES.output &&
      (sourceNode.data.descriptor as InputNodeDescriptor).type
        .charAt(0)
        .toLowerCase() !==
        (
          targetNode.data.descriptor as TransformNodeDescriptor
        ).familyClassification
          .charAt(0)
          .toLowerCase()
    ) {
      return {
        isValid: false,
        errorMessage: `Cannot connect input node of type ${
          (sourceNode.data.descriptor as InputNodeDescriptor).type
        } to a ${targetNode.data.descriptor.displayName} transform node`,
        severity: "error",
      };
    }
    return { isValid: true };
  }

  protected checkTransformScopeMatch(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const { sourceNode, targetNode } = context;

    // Only applies when an input node feeds a transform node.
    if (
      sourceNode.type !== NODE_TYPES.input ||
      targetNode.type === NODE_TYPES.output ||
      targetNode.type === NODE_TYPES.composer
    ) {
      return { isValid: true };
    }

    const targetScope = (targetNode.data.descriptor as TransformNodeDescriptor)
      .transformScope;

    // Can't enforce a scope that isn't declared; don't block.
    if (!targetScope) {
      return { isValid: true };
    }

    const isFullFileSource = connection.sourceHandle === PARENT_HANDLE_ID;

    if (isFullFileSource && targetScope !== TransformScope.FULL) {
      return {
        isValid: false,
        errorMessage:
          "A full-file handle can only connect to a full-file transform node.",
        severity: "error",
      };
    }

    if (!isFullFileSource && targetScope !== TransformScope.GRANULAR) {
      return {
        isValid: false,
        errorMessage:
          "Granular data can only connect to a granular transform node.",
        severity: "error",
      };
    }

    return { isValid: true };
  }

  protected checkOutputTypeMatch(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const sourceNode = context.sourceNode;
    const targetNode = context.targetNode;

    if (
      targetNode.type === NODE_TYPES.output ||
      targetNode.type === NODE_TYPES.composer
    ) {
      // Check if the output node already has an incoming edge
      const existingEdge = context.existingEdges.find(
        (edge) => edge.target === targetNode.id
      );

      if (existingEdge) {
        return {
          isValid: false,
          errorMessage: `The selected output node already has a connection. Attach the transform node to an available output or composer node.`,
          severity: "error",
        };
      }

      const outputType = (targetNode.data.descriptor as OutputNodeDescriptor)
        .type;

      if (outputType && outputType !== FileType.UNDETECTED) {
        const sourceFamily = (
          sourceNode.data.descriptor as TransformNodeDescriptor
        ).familyClassification;

        if (sourceFamily !== targetNode.data.descriptor.familyClassification) {
          return {
            isValid: false,
            errorMessage: `The selected output node has an existing edge connection. Attach the transform node to an available output node`,
            severity: "error",
          };
        }
      }
    }

    return { isValid: true };
  }

  protected checkNodesExist(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    if (!context.sourceNode || !context.targetNode) {
      return {
        isValid: false,
        errorMessage: "Source or target node does not exist",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  protected checkDuplicateConnection(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const isDuplicate = context.existingEdges.some(
      (edge) =>
        edge.source === connection.source &&
        edge.target === connection.target &&
        edge.sourceHandle === connection.sourceHandle &&
        edge.targetHandle === connection.targetHandle
    );

    if (isDuplicate) {
      return {
        isValid: false,
        errorMessage: "This connection already exists",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  protected checkInputToOutputConnection(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const { sourceNode, targetNode } = context;

    // Input nodes cannot connect directly to output nodes
    // They must go through a transform node first
    if (
      sourceNode.type === NODE_TYPES.input &&
      (targetNode.type === NODE_TYPES.output ||
        targetNode.type === NODE_TYPES.composer)
    ) {
      return {
        isValid: false,
        errorMessage: "Input nodes can only be connected to transform nodes",
        severity: "warning",
      };
    }

    return { isValid: true };
  }

  protected checkForCycles(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // Build adjacency list with the new connection
    const adjacencyList = new Map<string, Set<string>>();

    // Add existing edges
    context.existingEdges.forEach((edge) => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, new Set());
      }
      (adjacencyList.get(edge.source) as Set<string>).add(edge.target);
    });

    // Add the proposed connection
    if (!adjacencyList.has(connection.source)) {
      adjacencyList.set(connection.source, new Set());
    }
    (adjacencyList.get(connection.source) as Set<string>).add(
      connection.target
    );

    // Check for cycle using DFS
    const hasCycle = this.detectCycle(adjacencyList, connection.source);

    if (hasCycle) {
      return {
        isValid: false,
        errorMessage: "This connection would create a cycle",
        severity: "error",
      };
    }

    return { isValid: true };
  }

  private detectCycle(
    adjacencyList: Map<string, Set<string>>,
    startNode: string
  ): boolean {
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const dfs = (node: string): boolean => {
      visited.add(node);
      recursionStack.add(node);

      const neighbors = adjacencyList.get(node) || new Set();
      // eslint-disable-next-line no-loops/no-loops
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (dfs(neighbor)) {
            return true;
          }
        } else if (recursionStack.has(neighbor)) {
          return true; // Cycle detected
        }
      }

      recursionStack.delete(node);
      return false;
    };

    return dfs(startNode);
  }

  // ============================================================================
  // GENERAL NODE RULES (apply to all transforms)
  // ============================================================================

  protected runGeneralNodeRules(
    context: NodeRuleContext
  ): RuleValidationResult {
    // Rule 1: Node must have valid title
    const titleResult = this.checkNodeTitle(context);
    if (!titleResult.isValid) return titleResult;

    // Rule 2: Node must have descriptor
    const descriptorResult = this.checkNodeDescriptor(context);
    if (!descriptorResult.isValid) return descriptorResult;

    // Rule 3: Node must have at least one entry
    const entriesResult = this.checkNodeHasEntries(context);
    if (!entriesResult.isValid) return entriesResult;

    return { isValid: true };
  }

  protected checkNodeTitle(context: NodeRuleContext): RuleValidationResult {
    const title = context.node.data.title;

    if (!title || title.trim() === "") {
      return {
        isValid: false,
        errorMessage: "Node must have a title",
        severity: "error",
      };
    }

    // Validate title format (alphanumeric, spaces, hyphens, periods, underscores)
    const validTitleRegex = /^[\w\-. ]+$/;
    if (!validTitleRegex.test(title)) {
      return {
        isValid: false,
        errorMessage:
          "Node title contains invalid characters. Only letters, numbers, spaces, hyphens, periods, and underscores are allowed.",
        severity: "error",
      };
    }

    return { isValid: true };
  }

  protected checkNodeDescriptor(
    context: NodeRuleContext
  ): RuleValidationResult {
    if (!context.node.data.descriptor) {
      return {
        isValid: false,
        errorMessage: "Node must have a descriptor",
        severity: "error",
      };
    }
    return { isValid: true };
  }

  protected checkNodeHasEntries(
    context: NodeRuleContext
  ): RuleValidationResult {
    if (!context.node.data.entries || context.node.data.entries.length === 0) {
      return {
        isValid: false,
        errorMessage: "Node must have at least one entry",
        severity: "warning",
      };
    }
    return { isValid: true };
  }

  // ============================================================================
  // GENERAL EDGE RULES (apply to all transforms)
  // ============================================================================

  protected runGeneralEdgeRules(
    context: EdgeRuleContext
  ): RuleValidationResult {
    // Rule 1: Edge must have valid source and target
    const validEndpointsResult = this.checkEdgeEndpoints(context);
    if (!validEndpointsResult.isValid) return validEndpointsResult;

    // Rule 2: Source and target nodes must exist
    const nodesExistResult = this.checkEdgeNodesExist(context);
    if (!nodesExistResult.isValid) return nodesExistResult;

    return { isValid: true };
  }

  protected checkEdgeEndpoints(context: EdgeRuleContext): RuleValidationResult {
    const { edge } = context;

    if (!edge.source || !edge.target) {
      return {
        isValid: false,
        errorMessage: "Edge must have both source and target",
        severity: "error",
      };
    }

    if (!edge.sourceHandle || !edge.targetHandle) {
      return {
        isValid: false,
        errorMessage: "Edge must specify source and target handles",
        severity: "error",
      };
    }

    return { isValid: true };
  }

  protected checkEdgeNodesExist(
    context: EdgeRuleContext
  ): RuleValidationResult {
    const { edge, allNodes } = context;

    const sourceExists = allNodes.some((node) => node.id === edge.source);
    const targetExists = allNodes.some((node) => node.id === edge.target);

    if (!sourceExists || !targetExists) {
      return {
        isValid: false,
        errorMessage: "Edge references non-existent nodes",
        severity: "error",
      };
    }

    return { isValid: true };
  }

  // ============================================================================
  // ABSTRACT METHODS (must be implemented by subclasses)
  // ============================================================================

  /**
   * Run transform-specific connection rules
   * Must be implemented by subclasses for their specific transform logic
   */
  protected abstract runSpecificConnectionRules(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult;

  /**
   * Run transform-specific node rules
   * Must be implemented by subclasses for their specific transform logic
   */
  protected abstract runSpecificNodeRules(
    context: NodeRuleContext
  ): RuleValidationResult;

  /**
   * Run transform-specific edge rules
   * Must be implemented by subclasses for their specific transform logic
   */
  protected abstract runSpecificEdgeRules(
    context: EdgeRuleContext
  ): RuleValidationResult;
}

// ============================================================================
// PX (Protobuf -> XML) TRANSFORM RULES
// ============================================================================

/**
 * Rules specific to Protobuf -> XML transformations
 */
export class PXTransformRules extends TransformEngineRules {
  protected runSpecificConnectionRules(
    connection: Connection,
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // PX Rule 1: Check data type compatibility
    const typeCompatibilityResult = this.checkPXTypeCompatibility(context);
    if (!typeCompatibilityResult.isValid) return typeCompatibilityResult;

    // PX Rule 2: Check for required field mappings
    const requiredFieldsResult = this.checkPXRequiredFields(context);
    if (!requiredFieldsResult.isValid) return requiredFieldsResult;

    // PX Rule 3: Validate protobuf to XML path structure
    const pathStructureResult = this.checkPXPathStructure(context);
    if (!pathStructureResult.isValid) return pathStructureResult;

    return { isValid: true };
  }

  protected runSpecificNodeRules(
    context: NodeRuleContext
  ): RuleValidationResult {
    // PX Rule 1: Input nodes must be protobuf type
    const inputTypeResult = this.checkPXInputNodeType(context);
    if (!inputTypeResult.isValid) return inputTypeResult;

    // PX Rule 2: Output nodes must be XML type
    const outputTypeResult = this.checkPXOutputNodeType(context);
    if (!outputTypeResult.isValid) return outputTypeResult;

    // PX Rule 3: Validate protobuf field names
    const fieldNamesResult = this.checkPXFieldNames(context);
    if (!fieldNamesResult.isValid) return fieldNamesResult;

    return { isValid: true };
  }

  protected runSpecificEdgeRules(
    context: EdgeRuleContext
  ): RuleValidationResult {
    // PX Rule 1: Validate protobuf to XML mapping structure
    const mappingStructureResult = this.checkPXMappingStructure(context);
    if (!mappingStructureResult.isValid) return mappingStructureResult;

    // PX Rule 2: Check for data loss in conversion
    const dataLossResult = this.checkPXDataLoss(context);
    if (!dataLossResult.isValid) return dataLossResult;

    return { isValid: true };
  }

  // PX-specific validation methods

  private checkPXTypeCompatibility(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    const { sourceHandle, targetHandle } = context;

    // Check if source protobuf type can be converted to target XML type
    // This would check the actual data types from the path information

    // Example: Check if connecting a protobuf repeated field to XML element
    const sourcePath = sourceHandle?.path;
    const targetPath = targetHandle?.path;

    if (sourcePath && targetPath) {
      // Add specific type checking logic here
      // For now, return valid
    }

    return { isValid: true };
  }

  private checkPXRequiredFields(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // Check if all required protobuf fields are being mapped to XML
    // This would require metadata about which fields are required

    return { isValid: true };
  }

  private checkPXPathStructure(
    context: ConnectionRuleContext
  ): RuleValidationResult {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { sourceHandle, targetHandle } = context;

    // Validate that the path structure makes sense for PX transformation
    // E.g., protobuf nested messages should map to XML nested elements

    return { isValid: true };
  }

  private checkPXInputNodeType(context: NodeRuleContext): RuleValidationResult {
    const descriptor = context.node.data.descriptor;

    // Check if this is an input node and if so, verify it's protobuf type
    if (this.isInputNode(descriptor)) {
      // Add logic to check if the file type is protobuf
      // For now, return valid
    }

    return { isValid: true };
  }

  private checkPXOutputNodeType(
    context: NodeRuleContext
  ): RuleValidationResult {
    const descriptor = context.node.data.descriptor;

    // Check if this is an output node and if so, verify it's XML type
    if (this.isOutputNode(descriptor)) {
      // Add logic to check if the file type is XML
      // For now, return valid
    }

    return { isValid: true };
  }

  private checkPXFieldNames(context: NodeRuleContext): RuleValidationResult {
    // Validate that protobuf field names follow conventions
    const entries = context.node.data.entries;

    // eslint-disable-next-line no-loops/no-loops
    for (const entry of entries) {
      if (entry.label) {
        // Check if field name follows protobuf conventions
        // (lowercase with underscores)
        const isValidProtobufName = /^[a-z][a-z0-9_]*$/.test(entry.label);

        if (!isValidProtobufName && entry.path?.pathType === PathType.INPUT) {
          return {
            isValid: false,
            warningMessage: `Field name "${entry.label}" does not follow protobuf naming conventions (lowercase with underscores)`,
            severity: "warning",
          };
        }
      }
    }

    return { isValid: true };
  }

  private checkPXMappingStructure(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context: EdgeRuleContext
  ): RuleValidationResult {
    // Validate the overall mapping structure for PX transformation
    // This could check for common patterns or anti-patterns

    return { isValid: true };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private checkPXDataLoss(context: EdgeRuleContext): RuleValidationResult {
    // Check if the protobuf to XML conversion might result in data loss
    // E.g., protobuf has numeric types that XML might not preserve

    return { isValid: true };
  }

  // Helper methods

  private isInputNode(
    descriptor: NodeDescriptor | InputNodeDescriptor | OutputNodeDescriptor
  ): descriptor is InputNodeDescriptor {
    return "associatedFileId" in descriptor; // Unique to InputNodeDescriptor
  }

  private isOutputNode(
    descriptor: NodeDescriptor | InputNodeDescriptor | OutputNodeDescriptor
  ): descriptor is OutputNodeDescriptor {
    return "isTransformReady" in descriptor; // Unique to OutputNodeDescriptor
  }
}

// ============================================================================
// RULE ENGINE FACTORY
// ============================================================================

/**
 * Factory for creating appropriate rule engine instances
 */
export class TransformEngineRuleFactory {
  /**
   * Create a rule engine for the given transform type
   * @param transformType The type of transform (e.g., "PX", "XJ", etc.)
   * @returns Appropriate rule engine instance
   */
  public static createRuleEngine(transformType: string): TransformEngineRules {
    switch (transformType.toUpperCase()) {
      case "PX":
        return new PXTransformRules();
      // Add more transform types here as needed
      // case "XJ":
      //   return new XJTransformRules();
      default:
        // Return a default implementation with no specific rules
        return new DefaultTransformRules();
    }
  }
}

/**
 * Default transform rules (no specific rules, only general ones)
 */
class DefaultTransformRules extends TransformEngineRules {
  protected runSpecificConnectionRules(): RuleValidationResult {
    return { isValid: true };
  }

  protected runSpecificNodeRules(): RuleValidationResult {
    return { isValid: true };
  }

  protected runSpecificEdgeRules(): RuleValidationResult {
    return { isValid: true };
  }
}
